<template>
  <div :class="controlClasses" class="mds-form-control product-characteristic-picture-input">
    <label :style="labelStyles" v-if="label">
      {{ label }}
      <span v-if="showAsterisk" class="asterisk">*</span>
    </label>

    <VFilesUploadButton
      :entity="entityPointer"
      :disabled="isDisabled"
      theme="link"
      icon-postfix
      multiple
      class="upload-link"
      @click="onUploadClick"
    >
      <slot>Загрузить файлы</slot>
    </VFilesUploadButton>

    <div class="inner">
      <!-- Прикреплённые файлы: миниатюра с крестиком открепления поверх -->
      <div
        v-if="currentValue.length > 0"
        class="attached-files"
        :class="attachedFilesLayout"
      >
        <div
          v-for="(pointer, index) in currentValue"
          :key="pointer.id"
          class="attached-file"
        >
          <VPictureThumbnail
            :entity-pointer="pointer"
            size="medium"
          />

          <VButton
            v-if="showClear"
            tabindex="-1"
            class="remove"
            @click.stop="onRemove(index)"
            material="close"
          />
        </div>
      </div>

      <div class="attached-files empty grey" v-else>
        Изображений нет
      </div>

      <div class="messages-row">
        <ul class="error" v-if="validation.isValid === false">
          <li
            v-for="(error, index) in validation.errors"
            :key="'error' + index"
          >
            {{ error }}
          </li>
        </ul>

        <ul class="warning" v-if="validation.warnings.length > 0">
          <li
            v-for="(warning, index) in validation.warnings"
            :key="'warning' + index"
          >
            {{ warning }}
          </li>
        </ul>

        <div class="helper-text" v-if="isEntityNotReady">
          Сначала сохраните запись — прикрепить файл можно только после её создания
        </div>

        <div class="helper-text" v-else-if="hasHelperTextSlot">
          <!-- @slot Слот вспомогательного текста -->
          <slot name="helper-text" />
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" src="./ProductCharacteristicsPictureInput.scss" />

<script lang="ts">
import VBaseControl from "@/core/services/VBaseControl";
import VFilesUploadButton
  from "@/core/services/Files/components/VFilesUploadButton/VFilesUploadButton.vue";
import EntityPointer from "@/domains/EntityPointer/EntityPointer";
import FileDescriptor from "@/core/services/Files/FileDescriptor";
import FilesService from "@/core/services/Files/FilesService";

/** До скольких картинок включительно раскладываем их в строку. */
const ROW_LAYOUT_MAX_COUNT = 5;

export default defineComponent({
  name: "ProductCharacteristicsPictureInput",

  components: { VFilesUploadButton },

  extends: VBaseControl,

  props: {
    // Базовые пропы (label, placeholder, disabled, required, disabledReasons, layout и пр.) — из VBaseControl.

    /** v-model — список указателей на прикреплённые файлы. */
    modelValue: {
      type:    Array as PropType<EntityPointer[]>,
      default: undefined,
    },

    /**
     * Односторонний ввод значения. Используется вместе с @input, когда
     * присваивание в сущность делается вручную в обработчике родителя.
     * Читается только если modelValue не передан.
     */
    value: {
      type:    Array as PropType<EntityPointer[]>,
      default: undefined,
    },

    /**
     * Сущность, к которой прикрепляется файл (например, product).
     * Передаётся в VFilesUploadButton и служит фильтром: аплоад с другой entity
     * этот контрол игнорирует. Пока у сущности нет id (не сохранена) —
     * прикрепление недоступно.
     */
    entityPointer: {
      type:    Object as PropType<EntityPointer>,
      default: null,
    },
  },

  emits: ["update:modelValue", "input", "blur"],

  computed: {
    /**
     * Текущее значение контрола. Приоритет у modelValue; value читается,
     * когда modelValue не передан. Если не передан ни один из пропов —
     * список пуст.
     */
    currentValue(): EntityPointer[] {
      if (this.modelValue !== undefined) {
        return this.modelValue;
      } else if (this.value !== undefined) {
        return this.value;
      } else {
        return [];
      }
    },

    /**
     * Сущность ещё не создана: не передана или без id. Пока так — прикреплять
     * файл нельзя (беку не к чему привязать аплоад), кнопка заблокирована.
     */
    isEntityNotReady(): boolean {
      return this.entityPointer === null
        || this.entityPointer.id === null
        || this.entityPointer.id === undefined;
    },

    /** До пяти картинок ставим в строку, дальше — в колонку. */
    attachedFilesLayout(): string {
      if (this.currentValue.length > ROW_LAYOUT_MAX_COUNT) {
        return 'column';
      } else {
        return 'row';
      }
    },

    hasHelperTextSlot(): boolean {
      return this.$slots["helper-text"] !== undefined;
    },

    /** Расширяем базовые классы своими. */
    controlClasses(): string[] {
      const classes = this.getBaseControlClasses();
      if (this.currentValue.length === 0) classes.push("empty");
      return classes;
    },

    /**
     * Кнопка выключена пропом disabled ИЛИ пока сущность не создана
     * (нельзя прикрепить файл к тому, чего ещё нет на беке).
     */
    isDisabled(): boolean {
      return this.disabled === true || this.isEntityNotReady === true;
    },

    /**
     * Крестики открепления показываются, когда контрол не выключен пропом disabled.
     * От isEntityNotReady не зависит: даже если сущность пересоздали, уже
     * привязанные файлы должно быть можно отвязать.
     */
    showClear(): boolean {
      return this.disabled === false;
    },
  },

  beforeUnmount() {
    // Страховка: если контрол размонтируют с открытым выбором файла, слушатель
    // должен быть снят. При штатной работе он снимается в onFilesUploaded.
    this.$core.eventBus.FilesUploaded.removeEventListener(this.onFilesUploaded);
  },

  methods: {
    /** Единая точка эмита значения: update:modelValue для v-model, input — для ручного присваивания. */
    emitValue(pointers: EntityPointer[]): void {
      this.$emit("update:modelValue", pointers);
      this.$emit("input", pointers);
      this.$emit("blur", pointers);
    },

    /**
     * Клик по кнопке загрузки. Сам выбор файла и аплоад делает
     * VFilesUploadButton, нам остаётся отметить касание контрола и подписаться
     * на результат — событие FilesUploaded.
     */
    onUploadClick(): void {
      this.validation.markTouched();

      this.$core.eventBus.FilesUploaded.addEventListener(this.onFilesUploaded);
    },

    /** Загруженные файлы дописываются к уже прикреплённым — контрол принимает несколько. */
    onFilesUploaded({ files }: { files: FileDescriptor[]; entity: EntityPointer }): void {
      const attached = files.map(file => new EntityPointer("file", file.id, file.name));

      this.emitValue([...this.currentValue, ...attached]);

      // Слушатель нужен только на время загрузки — снимаем сразу после записи
      // файлов, чтобы он не жил до следующего нажатия на кнопку.
      this.$core.eventBus.FilesUploaded.removeEventListener(this.onFilesUploaded);

      // Валидируем после того, как обновлённый modelValue доедет обратно в проп,
      // иначе validate() прочитает ещё старое значение и ошибка не сбросится.
      this.$nextTick(() => this.validate());
    },

    async onRemove(index: number): Promise<void> {
      const pointer  = this.currentValue[index];
      const previous = [...this.currentValue];

      // Оптимистично убираем из UI.
      const result = [...this.currentValue];
      result.splice(index, 1);
      this.emitValue(result);

      // Получаем дескриптор и удаляем файл на беке.
      const fileDescriptor = await FilesService.get(pointer.id);

      let isOk = false;
      if (fileDescriptor !== null) {
        isOk = await FilesService.deleteFile(fileDescriptor);
      }

      // Файл не удалён — возвращаем пред-стейт.
      if (isOk === false) {
        this.emitValue(previous);
      } else {
        this.validate();
      }
    },

    /**
     * Запускает валидацию текущего значения через Validation.
     * Гейт touched+dirty — контрол сам решает, когда показывать ошибки.
     */
    async validate(): Promise<void> {
      this.validation.markDirty();
      if (this.validation.isTouched === false || this.validation.isDirty === false) return;
      if (this.isEntityNotReady) return;
      await this.validation.validate(this.currentValue);
    },
  },
});
</script>
